/*
 * SchemaComponent - Modern Professional UI Component
 * 
 * Applied Professional Styling Features:
 * ✅ Clean, minimalist design with proper spacing
 * ✅ Contemporary color palette and typography
 * ✅ Improved visual hierarchy and accessibility
 * ✅ Modern card-based layout with subtle shadows
 * ✅ Enhanced interactive elements with smooth animations
 * ✅ Consistent design tokens and spacing system
 * ✅ Professional button design with proper states
 * ✅ Improved form controls with modern styling
 */

import React, { useCallback, useState } from 'react';
import { View, Text, Pressable, Dimensions, Platform, StyleSheet } from 'react-native';
import { Input, TextArea, XStack } from 'tamagui';
import * as SVG from 'react-native-svg';
import * as Animatable from 'react-native-animatable';
import { ADD_DATA_ITEM, CHANGE_ITEM_DATA_TITLE, CHANGE_ITEM_DATA_TYPE, CHANGE_SELECTED_ITEM, REMOVE_DATA_ITEM, SIM_GESTURES, TOOLBAR_ITEM_DROPPED } from '@reduxLocal/actions/types';
import dataTypes from '../utils/dataTypes';
import { Entypo } from "@expo/vector-icons";
import ExamplePreviewHandler from './elements/ExamplePreviewHandler';
import { Feather, Fontisto, AntDesign, Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import AdditionalOptions from './SchemaComponent/AdditionalOptions';
import parseHeader from '@utils/stringUtils/parseHeader';
import parseByDelimiter from '@utils/parseByDelimiter';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { debounce, uniqueId } from 'lodash';
import { LinearGradient } from 'expo-linear-gradient';
import { H2, SizableText } from 'tamagui';
import { Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SchemaComponent {
  visible?: boolean,
  dataField?: any,
  indexLength: number,
  currentIndex: number,
  interaction?: boolean,
  toolbarRef?: any,
  modalState?: number,
  width?: number,
  callback?: Function
}


const SchemaComponent = ({ callback, modalState, visible, dataField, indexLength, currentIndex, interaction, toolbarRef, width }: SchemaComponent) => {

  // Remove local dataType state - use dataField.title directly
  const [dataPlaceHolder, setDataPlaceHolder] = useState('');
  const [selected, setSelected] = useState(false);
  const [topSelected, setTopSelected] = useState(false);
  // const selectedItem = useSelector((state: DefaultRootState) => state.toolbar.selectedItem);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(dataTypes);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [gestureActive, setGestureActive] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [options, setOptions] = useState(false);
  const theme = themeSelector();
  const uid = uniqueId();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Calculate string similarity using Levenshtein distance (simple implementation)
  const calculateSimilarity = React.useCallback((str1: string, str2: string): number => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();

    // Exact match
    if (s1 === s2) return 1.0;

    // One string contains the other
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;

    // Calculate simple similarity based on common characters
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    // Count matching characters
    let matches = 0;
    for (let i = 0; i < shorter.length; i++) {
      if (longer.includes(shorter[i])) {
        matches++;
      }
    }

    // Base similarity on character overlap
    const baseSimilarity = matches / longer.length;

    // Boost similarity if strings start with same characters
    const startMatch = s1.startsWith(s2.substring(0, Math.min(3, s2.length))) ||
      s2.startsWith(s1.substring(0, Math.min(3, s1.length)));
    if (startMatch) {
      return Math.min(1.0, baseSimilarity + 0.2);
    }

    return baseSimilarity;
  }, []);

  // Find best match using fuzzy inference
  const findBestMatch = React.useCallback((inputType: string, validValues: string[]): string | null => {
    const inputLower = inputType.toLowerCase();

    // Calculate similarity scores for all valid values
    const scores = validValues.map(value => ({
      value,
      score: calculateSimilarity(inputLower, value.toLowerCase())
    }));

    // Sort by score (highest first)
    scores.sort((a, b) => b.score - a.score);

    // Return best match if similarity is above threshold (0.3)
    const bestMatch = scores[0];
    if (bestMatch && bestMatch.score >= 0.3) {
      return bestMatch.value;
    }

    return null;
  }, [calculateSimilarity]);

  // Normalize data type value to ensure it's valid for the Select component
  const normalizeDataType = React.useCallback((type: string | undefined): string => {
    if (!type || type === 'EMPTY') return '';

    // Get all valid values from dataTypes
    const validValues = dataTypes.map(item => item.value);

    // If already valid, return as is
    if (validValues.includes(type)) {
      return type;
    }

    // Map legacy/invalid values to valid ones (exact mappings first)
    const typeMapping: Record<string, string> = {
      'date': 'dateTime',
      'datetime': 'dateTime',
      'dropdown': 'dropDown',
      'checkbox': 'checkBox',
      'checkboxgroup': 'checkGroup',
      'radiobutton': 'radioButton',
      'textarea': 'textArea',
      'fileupload': 'file',
      'dataset': 'dataSet',
      'datasetreference': 'dataSet',
      'integer': 'number',
      'float': 'number',
      'decimal': 'number',
      'num': 'number',
      'bool': 'boolean',
      'text': 'string',
      'str': 'string',
    };

    // Check exact mapping first
    const normalized = typeMapping[type.toLowerCase()];
    if (normalized && validValues.includes(normalized)) {
      return normalized;
    }

    // Use fuzzy matching to find best match
    const bestMatch = findBestMatch(type, validValues);
    if (bestMatch) {
      return bestMatch;
    }

    // Special handling for common patterns
    const typeLower = type.toLowerCase();

    // If contains "number", "num", "serial", "id", "code" -> likely string (for identifiers)
    if (typeLower.includes('number') || typeLower.includes('num') ||
      typeLower.includes('serial') || typeLower.includes('id') ||
      typeLower.includes('code') || typeLower.includes('uuid')) {
      return 'string';
    }

    // If contains "date" or "time" -> dateTime
    if (typeLower.includes('date') || typeLower.includes('time')) {
      return 'dateTime';
    }

    // If contains "data" -> could be dataSet or dateTime (prefer dataSet)
    if (typeLower.includes('data')) {
      return 'dataSet';
    }

    // If contains "file", "upload", "document" -> file
    if (typeLower.includes('file') || typeLower.includes('upload') || typeLower.includes('document')) {
      return 'file';
    }

    // If contains "image", "photo", "picture" -> image
    if (typeLower.includes('image') || typeLower.includes('photo') || typeLower.includes('picture')) {
      return 'image';
    }

    // If contains "location", "address", "place", "geo" -> location
    if (typeLower.includes('location') || typeLower.includes('address') ||
      typeLower.includes('place') || typeLower.includes('geo')) {
      return 'location';
    }

    // Default to string for unknown types (most flexible)
    return 'string';
  }, [findBestMatch]);

  const [dataTypeField, setDataTypeField] = useState(() => normalizeDataType(dataField?.type));

  // CRITICAL: Use local state for input to prevent Redux re-renders on every keystroke
  // Display in title case, but store in snake_case in Redux
  const getDisplayTitle = React.useCallback((snakeCaseTitle: string) => {
    if (!snakeCaseTitle) return '';
    // Convert snake_case to title case for display
    return parseByDelimiter(snakeCaseTitle) || '';
  }, []);

  const [localTitle, setLocalTitle] = useState(() => getDisplayTitle(dataField?.title || ''));
  const titleInputRef = React.useRef<string>('');

  // Sync localTitle with dataField.title only when it changes externally (not from our own updates)
  React.useEffect(() => {
    // Only update if the value is different and it's not from our own input
    // Convert from snake_case (Redux) to title case (display)
    const displayTitle = getDisplayTitle(dataField?.title || '');
    // Only sync if Redux value changed externally (not from our own update)
    if (dataField?.title !== titleInputRef.current && displayTitle !== localTitle) {
      setLocalTitle(displayTitle);
      titleInputRef.current = dataField?.title || '';
    }
  }, [dataField?.title, getDisplayTitle, localTitle]);

  // Sync dataTypeField with dataField.type only, with normalization
  // Use a ref to track the last Redux value to prevent unnecessary updates
  const lastReduxTypeRef = React.useRef<string | undefined>(dataField?.type);

  React.useEffect(() => {
    // Only normalize if the Redux value actually changed
    if (dataField?.type !== lastReduxTypeRef.current) {
      lastReduxTypeRef.current = dataField?.type;
      const normalizedType = normalizeDataType(dataField?.type);
      // Only update local state if different (don't auto-update Redux to avoid loops)
      if (normalizedType !== dataTypeField) {
        setDataTypeField(normalizedType);
      }
    }
  }, [dataField?.type, normalizeDataType, dataTypeField]);

  // Removed debug logging for better performance


  const handlePresentModalPress = useCallback(() => {
    toolbarRef.current?.present();
  }, []);

  const handleCloseModal = useCallback(() => {
    toolbarRef.current?.close()
  }, []);

  // useFocusEffect(React.useCallback(() => { if (modalState === -1 || Platform.OS === 'web') { setIsSelected(false) } }, [modalState]));

  const openArrow = () => { return (<View style={{ flex: 1, alignSelf: 'flex-end' }}><Entypo name={"chevron-thin-up"} style={[styles.menuItemIcon, { alignSelf: 'flex-end', paddingVertical: 7, alignContent: 'flex-end', alignItems: 'flex-end', justifyContent: 'flex-end' }]} size={20} color={'black'} ></Entypo></View>) }
  const closeArrow = () => { return (<View style={{ flex: 1, alignSelf: 'flex-end' }}><Entypo name={"chevron-thin-down"} style={[styles.menuItemIcon, { alignSelf: 'center', alignContent: 'center', alignItems: 'center', paddingVertical: 7 }]} size={20} color={'black'} ></Entypo></View>) }

  const rotateAdd = {
    from: {
      transform: [{ rotate: '0deg' }]
    },
    to: { transform: [{ rotate: '45deg' }] }
  }

  const unRotateAdd = {
    from: {
      transform: [{ rotate: '45deg' }]
    },
    to: { transform: [{ rotate: '0deg' }] }
  }


  const onDataTypeOpen = useCallback(() => {
    setCategoryOpen(false);
  }, [])

  const handleDataTypeChange = useCallback((value) => {
    // Ensure we only dispatch valid values
    const validValues = dataTypes.map(item => item.value);
    const normalizedValue = normalizeDataType(value);

    // Only dispatch if it's a valid value (or empty string for clearing)
    if (normalizedValue === '' || validValues.includes(normalizedValue)) {
      dispatch({
        type: CHANGE_ITEM_DATA_TYPE,
        payload: { newValue: normalizedValue || '' },
        index: currentIndex
      });
    }
  }, [dispatch, currentIndex, normalizeDataType]);

  // Create a stable debounced function that persists across renders
  // Using 500ms debounce for optimal performance - balances responsiveness with Redux overhead
  const debouncedTitleChange = React.useMemo(
    () => debounce((text: string) => {
      titleInputRef.current = text;
      const parsedHeader = parseHeader(text);
      dispatch({ type: CHANGE_ITEM_DATA_TITLE, payload: parsedHeader, index: currentIndex });
    }, 500), // 500ms debounce for better performance
    [dispatch, currentIndex]
  );

  // Cleanup debounced function on unmount
  React.useEffect(() => {
    return () => {
      debouncedTitleChange.cancel();
    };
  }, [debouncedTitleChange]);

  // Optimized: Update local state immediately, debounce Redux dispatch
  const handleTitleChange = useCallback((text: string) => {
    // Update local state immediately for instant UI feedback
    setLocalTitle(text);
    // Debounce Redux dispatch to avoid performance issues
    debouncedTitleChange(text);
  }, [debouncedTitleChange]);

  const handleTitleBlur = useCallback((e: any) => {
    // Cancel any pending debounced calls and dispatch immediately on blur
    debouncedTitleChange.cancel();
    const text = localTitle; // Use local state value

    // Only dispatch if there's a difference (safety check)
    if (text !== dataField.title) {
      titleInputRef.current = text;
      const parsedHeader = parseHeader(text);
      dispatch({ type: CHANGE_ITEM_DATA_TITLE, payload: parsedHeader, index: currentIndex });
    }
  }, [dispatch, currentIndex, dataField.title, localTitle, debouncedTitleChange]);


  return (
    <View style={{ 
      flex: 1,
      marginVertical: 8,
      paddingHorizontal: 4,
    }}>
      <View 
        style={{ 
          flexDirection: 'row', 
          zIndex: -currentIndex, 
          flex: 1, 
          alignSelf: 'stretch', 
          gap: 20,
          // backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.95)',
          borderRadius: 15,
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: theme ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.1)',
          transform: [{ scale: selected || options ? 1.02 : 1 }]
        }}
      >
        <View 
          style={{ 
            flex: 10, 
            flexDirection: 'row', 
            padding: 24, 
            alignSelf: 'stretch' 
          }}
        >
          <View style={{ 
            flex: 1, 
            minWidth: 100, 
            top: 85, 
            overflow: 'visible', 
            zIndex: currentIndex * -1 
          }}>
            {/* Modern Step Indicator */}
            <SVG.Svg 
              width={100} 
              height={100} 
              style={{ 
                zIndex: 10, 
                flex: 1, 
                marginRight: -20, 
                marginTop: -30, 
                overflow: 'visible', 
                minWidth: 50 
              }}
            >
              <SVG.Circle 
                cx={10} 
                cy={10} 
                r={5} 
                scale={3} 
                stroke={selected ? '#3B82F6' : '#E5E7EB'} 
                strokeWidth={3} 
                fill={selected ? '#3B82F6' : 'white'} 
              />
              <SVG.Text
                x={30}
                y={30}
                fontSize="16"
                fontWeight="700"
                fill={selected ? 'white' : '#374151'}
                textAnchor="middle"
                alignmentBaseline="middle"
              >
                {currentIndex + 1}
              </SVG.Text>
            </SVG.Svg>
            {
              visible ?
              <View style={{ zIndex: 10 }}>
                  <SVG.Svg width={100} height={100} style={{ zIndex: 2, flex: 1, marginRight: -20 }}>
                    <SVG.Circle cx={10} cy={10} r={5} scale={3} stroke={'rgba(90,192,234,33)'} strokeWidth={2} fill={'white'} />
                  <H2 style={{ top: -28, left: 0, position: "absolute" }}>{(currentIndex + 1).toString()}</H2>
                  </SVG.Svg>
                </View>
                :
                null
            }
            {/* Modern Connecting Line */}
            <SVG.Svg 
              width={100} 
              height={'100%'} 
              style={{ 
                flex: 1, 
                minHeight: 400, 
                top: 0, 
                position: 'absolute', 
                left: 25, 
                zIndex: -1 
              }}
            >
              <SVG.Defs>
                <SVG.LinearGradient id={`uniqueLineGradient${uid}`} x1="0" x2="0" y1="0" y2="1">
                  <SVG.Stop offset={'0%'} stopColor={'#3B82F6'} />
                  <SVG.Stop offset={options ? "99%" : '40%'} stopColor={'#3B82F6'} stopOpacity={0.1} />
                </SVG.LinearGradient>
              </SVG.Defs>
              <SVG.Rect 
                id={'connectionLine'} 
                width={3} 
                height={"100%"} 
                fill={`url(#uniqueLineGradient${uid})`} 
                rx={1.5}
              />
            </SVG.Svg>
          </View>
          <View style={{ flex: 10 }}>
              {/* Modern Interactive Actions */}
              <View style={{ 
                left: "-1%", 
                flex: 1, 
                flexDirection: 'row', 
                position: 'relative', 
                zIndex: -5, 
                maxWidth: "85%", 
                maxHeight: 50, 
                gap: 16,
                paddingVertical: 8 
              }}>
                {/* Delete Action */}
                <Pressable 
                  disabled={indexLength <= 0} 
                  onPress={() => dispatch({ type: REMOVE_DATA_ITEM, index: currentIndex })} 
                  style={[styles.centerEverything, { padding: 12, borderRadius: 16, top: -10, flexDirection: "row" }]}
                >
                  {({ hovered, pressed }) => (
                    <Animatable.View 
                      useNativeDriver 
                      transition={'backgroundColor'} 
                      style={[
                        styles.modernActionButton,
                        pressed && { transform: [{ scale: 0.96 }] },
                        hovered && { backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#EF4444' },
                        { 
                          top: 20, 
                          padding: 16, 
                          borderRadius: 16, 
                          flexDirection: "row", 
                          gap: 8, 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          minWidth: 100
                        }
                      ]}
                    >
                      <Entypo 
                        name="trash" 
                        size={20} 
                        style={{ marginTop: -2 }} 
                        color={indexLength <= 0 ? '#9CA3AF' : (hovered ? "#EF4444" : "#374151")} 
                      />
                      <Text style={[
                        styles.modernActionButtonText,
                        { color: indexLength <= 0 ? '#9CA3AF' : (hovered ? "#EF4444" : "#374151") }
                      ]}>
                        {t('Delete')}
                      </Text>
                    </Animatable.View>
                  )}
                </Pressable>

                {/* Options Action */}
                <Pressable 
                  disabled={indexLength <= 0} 
                  onPress={() => { if (callback) { callback() }; setOptions(!options); }} 
                  style={[styles.centerEverything, { padding: 12, borderRadius: 16, top: -10 }]}
                >
                  {({ hovered, pressed }) => (
                    <Animatable.View 
                      useNativeDriver 
                      transition={'backgroundColor'} 
                      style={[
                        styles.modernActionButton,
                        options && styles.modernActionButtonActive,
                        pressed && { transform: [{ scale: 0.96 }] },
                        hovered && !options && { backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3B82F6' },
                        { 
                          top: 20, 
                          padding: 16, 
                          borderRadius: 16, 
                          flexDirection: "row", 
                          gap: 8, 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          minWidth: 100
                        }
                      ]}
                    >
                      <Ionicons 
                        name="options" 
                        style={{ marginTop: -2 }} 
                        size={20} 
                        color={options ? 'white' : (hovered ? '#3B82F6' : '#374151')} 
                      />
                      <Text style={[
                        styles.modernActionButtonText,
                        options && styles.modernActionButtonTextActive,
                        hovered && !options && { color: '#3B82F6' }
                      ]}>
                        {t('Options')}
                      </Text>
                    </Animatable.View>
                  )}
                </Pressable>

                {/* Preview Action */}
                <Pressable 
                  disabled={indexLength <= 0} 
                  onPress={() => { setSelected(!selected) }} 
                  style={[styles.centerEverything, { padding: 12, borderRadius: 16, top: -10 }]}
                >
                  {({ hovered, pressed }) => (
                    <Animatable.View 
                      useNativeDriver 
                      transition={'backgroundColor'} 
                      style={[
                        styles.modernActionButton,
                        selected && styles.modernActionButtonActive,
                        pressed && { transform: [{ scale: 0.96 }] },
                        hovered && !selected && { backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3B82F6' },
                        { 
                          top: 20, 
                          padding: 16, 
                          borderRadius: 16, 
                          flexDirection: "row", 
                          gap: 8, 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          minWidth: 100
                        }
                      ]}
                    >
                      <Fontisto 
                        name="preview" 
                        size={20} 
                        style={{ marginTop: -2 }} 
                        color={selected ? 'white' : (indexLength <= 0 ? '#9CA3AF' : (hovered ? '#3B82F6' : '#374151'))} 
                      />
                      <Text style={[
                        styles.modernActionButtonText,
                        selected && styles.modernActionButtonTextActive,
                        indexLength <= 0 && { color: '#9CA3AF' },
                        hovered && !selected && { color: '#3B82F6' }
                      ]}>
                        {t('Preview')}
                      </Text>
                    </Animatable.View>
                  )}
                </Pressable>
              </View>
            <View style={{ flex: 3, flexDirection: "column-reverse" }}>
              {/* Modern Preview Section */}
              {
                selected &&
                <Animatable.View 
                  useNativeDriver 
                  animation={selected ? 'fadeInDown' : 'fadeInUp'} 
                  style={{ 
                    gap: 16, 
                    marginBottom: 32, 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 20, 
                    padding: 20, 
                    zIndex: -10, 
                    justifyContent: 'center', 
                    alignContent: 'center', 
                    minHeight: 140,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.08,
                    shadowRadius: 16,
                    elevation: 8,
                    backdropFilter: 'blur(20px)',
                    borderWidth: 1,
                    borderColor: 'rgba(59, 130, 246, 0.1)'
                  }}
                >
                  <View style={{
                    position: 'absolute',
                    top: -12,
                    left: 20,
                    backgroundColor: '#3B82F6',
                    paddingHorizontal: 16,
                    paddingVertical: 6,
                    borderRadius: 12,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 2
                  }}>
                    <Text style={{
                      color: 'white',
                      fontSize: 12,
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}>
                      Preview
                    </Text>
                  </View>
                  
                  <View style={{ 
                    flex: 1, 
                    backgroundColor: 'rgba(248, 250, 252, 0.9)', 
                    borderRadius: 10, 
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.05,
                    shadowRadius: 2,
                    elevation: 1,
                    justifyContent: 'center', 
                    alignContent: 'center', 
                    padding: 16,
                    marginTop: 8,
                    borderWidth: 1,
                    borderColor: 'rgba(226, 232, 240, 0.8)'
                  }}>
                    <View style={[styles.centerEverything, { 
                      margin: 12, 
                      flexDirection: 'row', 
                      minWidth: '100%',
                      gap: 16
                    }]}>
                      <Text 
                        adjustsFontSizeToFit 
                        style={{ 
                          flex: 2, 
                          minWidth: 70,
                          fontSize: 16,
                          fontWeight: '600',
                          color: '#374151',
                          textAlign: 'left'
                        }}
                      >
                          {localTitle || getDisplayTitle(dataField?.title || '')}
                      </Text>
                      <View style={{ 
                        flex: 5, 
                        justifyContent: 'center', 
                        alignContent: 'center', 
                        padding: 12,
                        backgroundColor: 'white',
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: 'rgba(226, 232, 240, 0.6)'
                      }}>
                        <ExamplePreviewHandler 
                          componentType={dataTypeField} 
                          defaultValue={null} 
                          parameters={{}} 
                        />
                      </View>
                    </View>
                  </View>
                </Animatable.View>
              }
              {/* Modern Data Input Section */}
              <View style={{ 
                flex: 3, 
                flexDirection: 'row', 
                marginBottom: 32, 
                zIndex: 10, 
                left: -20 
              }}>
                {/* Modern Pointer Arrow */}
                <View style={{
                  position: 'absolute',
                  left: -8,
                  top: 28,
                  width: 0,
                  height: 0,
                  borderTopWidth: 8,
                  borderTopColor: 'transparent',
                  borderBottomWidth: 8,
                  borderBottomColor: 'transparent',
                  borderRightWidth: 12,
                  borderRightColor: '#3B82F6',
                  zIndex: 1,
                  shadowColor: '#000',
                  shadowOffset: { width: 2, height: 0 },
                  shadowOpacity: 0.1,
                  shadowRadius: 4,
                  elevation: 2
                }} />
                
                <Animatable.View 
                  transition={'height'} 
                  style={{ 
                    padding: 4,
                    paddingBottom: 0, 
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.12,
                    shadowRadius: 16,
                    elevation: 8,
                    flex: 5, 
                    backgroundColor: '#3B82F6',
                    height: options ? 500 : (open ? 140 : 80), 
                    borderRadius: 20, 
                    alignSelf: 'stretch', 
                    minWidth: width,
                    borderWidth: 1,
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {/* Modern Input Container */}
                  <View style={{ 
                    padding: 8, 
                    borderRadius: 18, 
                    flex: 3, 
                    minHeight: 65, 
                    flexDirection: 'row', 
                    alignContent: 'flex-start', 
                    paddingVertical: 2,
                    gap: 20, 
                    alignSelf: 'stretch', 
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)'
                  }}>
                    {/* Modern Text Input - Using local state for performance */}
                    <Input
                      placeholder='Schema Item must have a name'
                      placeholderTextColor={'rgba(239, 68, 68, 0.8)'}
                      value={localTitle}
                      editable={interaction}
                      multiline
                      onFocus={() => { if (callback) callback() }}
                      onChangeText={handleTitleChange}
                      onBlur={handleTitleBlur}
                      style={{ 
                        top: -14, 
                        minHeight: 50, 
                        flex: 2.5, 
                        minWidth: "60%", 
                        paddingRight: 20,
                        fontWeight: '600', 
                        fontSize: 16,
                        backgroundColor: "transparent", 
                        marginRight: 20,
                        color: 'white',
                        textShadowColor: 'rgba(0, 0, 0, 0.3)',
                        textShadowOffset: { width: 0, height: 1 },
                        textShadowRadius: 2
                      }}
                    />
                    
                    {/* Modern Select Container */}
                    <View style={[styles.centerEverything, { 
                      flex: 2, 
                      left: -20, 
                      flexDirection: "row", 
                      gap: 12,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 12,
                      padding: 8,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.08,
                      shadowRadius: 4,
                      elevation: 2
                    }]}>
                      <Select
                        value={dataTypeField || ''}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          // Ensure we only set valid values
                          const validValues = dataTypes.map(item => item.value);
                          if (newValue === '' || validValues.includes(newValue)) {
                            setDataTypeField(newValue);
                            handleDataTypeChange(newValue);
                          }
                        }}
                        displayEmpty
                        style={{ 
                          minWidth: 200, 
                          flex: 3, 
                          maxHeight:50,
                          padding: '8px 16px', 
                          borderRadius: 8, 
                          borderWidth: 0, 
                          borderColor: 'transparent',
                          fontSize: 14,
                          fontWeight: '500',
                          color: '#374151',
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 220,
                              borderRadius: 12,
                              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                              border: '1px solid rgba(226, 232, 240, 0.8)'
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          if (!selected || selected === '') {
                            return <em style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Select type...</em>;
                          }
                          const selectedItem = dataTypes.find(item => item.value === selected);
                          return selectedItem ? selectedItem.label : selected;
                        }}
                      >
                        {dataTypes.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {
                        !dataTypeField || dataTypeField === "" ? (
                          <View style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            borderRadius: 8,
                            padding: 4
                          }}>
                            <AntDesign name="exclamationcircleo" size={16} color="#EF4444" />
                          </View>
                        ) : null
                      }
                    </View>
                  </View>
                  
                  {/* Modern Options Container */}
                  <View style={{ zIndex: -1, flex: 10, paddingVertical: 16 }}>
                    {
                      options &&
                      <Animatable.View 
                        useNativeDriver 
                        animation={'fadeIn'} 
                        style={{ 
                          flex: 1, 
                          backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)', 
                          borderRadius: 16, 
                          marginTop: 16, 
                          marginVertical: 12, 
                          padding: 20,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 4 },
                          shadowOpacity: 0.12,
                          shadowRadius: 16,
                          elevation: 8,
                          borderWidth: 1,
                          borderColor: theme ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <AdditionalOptions componentType={dataTypeField} index={currentIndex} item={dataField} />
                      </Animatable.View>
                    }
                  </View>
                </Animatable.View>
              </View>
            </View>
          </View>
        </View >
      </View>

      {/* Modern Floating Action Button */}
      {
        Platform.OS != 'web' &&
        <View style={{ position: 'relative', left: 0 }}>
          {
            interaction ?
              <Pressable 
                hitSlop={50} 
                onPress={() => {
                  if (!isSelected) {
                    setIsSelected(true);
                    dispatch({ type: CHANGE_SELECTED_ITEM, payload: currentIndex + 1 })
                    handlePresentModalPress();
                  } else if (isSelected) {
                    setIsSelected(false);
                    handleCloseModal();
                  }
                }} 
                style={{ position: 'absolute', top: -50, left: 31 }}
              >
                {({ pressed }) => (
                  <Animatable.View 
                    useNativeDriver 
                    duration={400} 
                    animation={isSelected ? rotateAdd : unRotateAdd} 
                    style={{ 
                      backgroundColor: !isSelected ? '#3B82F6' : '#EF4444', 
                      justifyContent: 'center', 
                      alignContent: 'center', 
                      borderRadius: 50, 
                      width: 42, 
                      height: 42,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                      elevation: 8,
                      borderWidth: 2,
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      transform: [{ scale: pressed ? 0.9 : 1 }]
                    }}
                  >
                    <Feather 
                      name="plus" 
                      size={24} 
                      color="white" 
                      style={{ alignSelf: 'center' }} 
                    />
                  </Animatable.View>
                )}
              </Pressable>
              :
              null
          }
        </View>
      }
    </View>
  )
}
export default SchemaComponent;

// Modern Professional Styles
const styles = StyleSheet.create({
  // Modern Professional Button Styles
  modernButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: 12,
  },
  
  modernButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  
  // Primary Action Button (Edit)
  primaryButton: {
    backgroundColor: '#3B82F6',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  
  primaryButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  
  // Secondary Action Button (Add Option)
  secondaryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backdropFilter: 'blur(10px)',
  },
  
  secondaryButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  
  // Success Button (Save)
  successButton: {
    backgroundColor: '#10B981',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  
  successButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  
  // Delete Button
  deleteButton: {
    backgroundColor: '#EF4444',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  
  deleteButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  
  // Disabled States
  disabledButton: {
    backgroundColor: 'rgba(156, 163, 175, 0.5)',
    borderColor: '#E5E7EB',
  },
  
  disabledButtonText: {
    color: '#9CA3AF',
  },
  
  // Modern Interactive Elements
  modernActionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.2)',
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    backdropFilter: 'blur(10px)',
  },
  
  modernActionButtonActive: {
    backgroundColor: '#3B82F6',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    transform: [{ scale: 0.98 }],
  },
  
  modernActionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'center',
  },
  
  modernActionButtonTextActive: {
    color: 'white',
  },

  centerEverything: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  
  // Legacy style for compatibility
  menuItemIcon: {
    fontSize: 16,
    color: '#374151'
  }
});
